import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, IconButton, Tooltip,
    CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, InputAdornment, Grid, Avatar, Stack, Card, CardContent,
    ToggleButton, ToggleButtonGroup, Grow, Autocomplete,
    FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../../config';
import Loading from '../../components/Loading';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HistoryIcon from '@mui/icons-material/History';
import ChatIcon from '@mui/icons-material/Chat';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import MapIcon from '@mui/icons-material/Map';
import GridOnIcon from '@mui/icons-material/GridOn';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import FolderIcon from '@mui/icons-material/Folder';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ClearIcon from '@mui/icons-material/Clear';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddIcon from '@mui/icons-material/Add';

function generateGuid() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

const CATEGORIES = [
    { key: 'pdf', label: 'PDF Documents', accept: '.pdf', icon: <PictureAsPdfIcon />, color: '#e53935' },
    { key: 'word', label: 'Word Documents', accept: '.doc,.docx', icon: <DescriptionIcon />, color: '#1565c0' },
    { key: 'map_pdf', label: 'Map PDFs', accept: '.pdf', icon: <MapIcon />, color: '#2e7d32' },
    { key: 'excel', label: 'Excel Sheets', accept: '.xls,.xlsx,.xlsm,.csv', icon: <GridOnIcon />, color: '#00695c' },
];

const FIELD_MAP = { pdf: 'pdf_files', word: 'word_files', map_pdf: 'map_pdf_files', excel: 'excel_files' };

const TYPE_CHIPS = [
    { key: 'pdf', label: 'PDF', color: '#e53935', icon: <PictureAsPdfIcon sx={{ fontSize: 14 }} /> },
    { key: 'word', label: 'Word', color: '#1565c0', icon: <DescriptionIcon sx={{ fontSize: 14 }} /> },
    { key: 'map_pdf', label: 'Map', color: '#2e7d32', icon: <MapIcon sx={{ fontSize: 14 }} /> },
    { key: 'excel', label: 'Excel', color: '#00695c', icon: <GridOnIcon sx={{ fontSize: 14 }} /> },
];

const WORK_STATUSES = ['New', 'In Progress', 'Completed', 'Cancelled'];

const WORK_STATUS_COLOR = {
    'New': '#1976d2',
    'In Progress': '#ff9800',
    'Completed': '#2e7d32',
    'Cancelled': '#f44336',
};

function Tendering() {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [viewMode, setViewMode] = useState('grid');
    const [openUpload, setOpenUpload] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadPhase, setUploadPhase] = useState('');
    const [name, setName] = useState('');
    const [countries, setCountries] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [countryId, setCountryId] = useState('');
    const [divisionId, setDivisionId] = useState('');
    const [filterCountry, setFilterCountry] = useState([]);
    const [filterDivision, setFilterDivision] = useState([]);
    const [filterStatus, setFilterStatus] = useState('All');
    const [files, setFiles] = useState({ pdf: [], word: [], map_pdf: [], excel: [] });
    const [filesDialogOpen, setFilesDialogOpen] = useState(false);
    const [filesSession, setFilesSession] = useState(null);
    const [filesList, setFilesList] = useState([]);
    const [filesLoading, setFilesLoading] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    const [dragOverCat, setDragOverCat] = useState(null);

    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

    const fetchSessions = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/tendering/sessions`, { headers });
            setSessions(res.data || []);
        } catch (err) {
            console.error('Error fetching tendering sessions:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchSessions(); }, []);

    useEffect(() => {
        const fetchSetup = async () => {
            try {
                const [cRes, dRes] = await Promise.all([
                    axios.get(`${API_URL}/setup/countries`, { headers }),
                    axios.get(`${API_URL}/setup/divisions`, { headers })
                ]);
                setCountries(cRes.data || []);
                setDivisions(dRes.data || []);
            } catch (err) {
                console.error('Error fetching countries/divisions:', err);
            }
        };
        fetchSetup();
    }, []);

    const resetUpload = () => {
        setName('');
        setCountryId('');
        setDivisionId('');
        setFiles({ pdf: [], word: [], map_pdf: [], excel: [] });
    };

    const handleSelectFiles = (category, fileList) => {
        const incoming = Array.from(fileList);
        setFiles(prev => {
            const merged = [...prev[category]];
            incoming.forEach(f => {
                if (!merged.some(x => x.name === f.name && x.size === f.size)) {
                    merged.push({ id: Math.random().toString(36).slice(2), name: f.name, size: f.size, file: f });
                }
            });
            return { ...prev, [category]: merged };
        });
    };

    const removeFile = (category, id) => {
        setFiles(prev => ({ ...prev, [category]: prev[category].filter(f => f.id !== id) }));
    };

    // --- Drag & drop per file-type tile ---
    const acceptsFile = (category, fileName) => {
        const cat = CATEGORIES.find(c => c.key === category);
        if (!cat) return false;
        const exts = cat.accept.split(',').map(s => s.trim().toLowerCase());
        const ext = ('.' + (fileName || '').split('.').pop()).toLowerCase();
        return exts.includes(ext);
    };

    const handleDragOver = (e, category) => {
        e.preventDefault();
        e.stopPropagation();
        if (dragOverCat !== category) setDragOverCat(category);
    };

    const handleDragLeave = (e, category) => {
        e.preventDefault();
        e.stopPropagation();
        if (dragOverCat === category) setDragOverCat(null);
    };

    const handleDrop = (e, category) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverCat(null);
        const dropped = Array.from(e.dataTransfer?.files || []);
        if (dropped.length === 0) return;
        const valid = dropped.filter(f => acceptsFile(category, f.name));
        const invalid = dropped.filter(f => !acceptsFile(category, f.name));
        if (valid.length > 0) handleSelectFiles(category, valid);
        if (invalid.length > 0) {
            const cat = CATEGORIES.find(c => c.key === category);
            alert(`These file(s) are not allowed in "${cat?.label}" (accepts ${cat?.accept}):\n` +
                  invalid.map(f => `• ${f.name}`).join('\n'));
        }
    };

    const totalFileCount = Object.values(files).reduce((a, list) => a + list.length, 0);

    const handleUpload = async () => {
        if (totalFileCount === 0) { alert('Please select at least one file to upload.'); return; }
        if (!countryId || !divisionId) { alert('Please select the Country and Division.'); return; }
        const guid = generateGuid();
        const formData = new FormData();
        formData.append('session_id', guid);
        if (name.trim()) formData.append('name', name.trim());
        formData.append('country_id', countryId);
        formData.append('division_id', divisionId);
        Object.entries(files).forEach(([cat, list]) => {
            list.forEach(f => formData.append(FIELD_MAP[cat], f.file));
        });
        setUploading(true);
        setUploadPhase('Uploading files...');
        try {
            const res = await axios.post(`${API_URL}/tendering/upload`, formData, {
                headers: { ...headers, 'Content-Type': 'multipart/form-data' },
                timeout: 600000
            });
            const sessionId = res.data?.session_id || guid;

            // Wait for the AI to finish building the vector store before opening the chat
            setUploadPhase('Processing documents with AI...');
            await waitForProcessing(sessionId);

            navigate(`/tendering/${sessionId}/chat`);
        } catch (err) {
            console.error('Upload error:', err);
            let msg = err.response?.data?.error || err.response?.data?.message;
            if (!msg) {
                if (err.code === 'ECONNABORTED') msg = 'The request timed out. Please try again or upload fewer/smaller files.';
                else if (err.message === 'Network Error') msg = 'Could not reach the server. Please check your connection and that the API is running.';
                else msg = err.message;
            }
            alert('Upload failed: ' + msg);
        } finally {
            setUploading(false);
            setUploadPhase('');
        }
    };

    // Poll the session until the AI finishes (status becomes Ready or Failed).
    // Waits up to 10 minutes, then continues regardless.
    const waitForProcessing = async (sessionGuid, maxAttempts = 300, intervalMs = 2000) => {
        for (let i = 0; i < maxAttempts; i++) {
            try {
                const res = await axios.get(`${API_URL}/tendering/sessions/${sessionGuid}`, { headers });
                const st = res.data?.status;
                if (st === 'Ready' || st === 'Failed') return st;
            } catch (err) {
                console.warn('Status poll failed, retrying...', err.message);
            }
            await new Promise(r => setTimeout(r, intervalMs));
        }
        return 'Timeout';
    };

    const fileTypeMeta = (ftype) => TYPE_CHIPS.find(c => c.key === ftype) || { label: 'File', color: '#64748b', icon: <DescriptionIcon sx={{ fontSize: 14 }} /> };

    const closePreview = () => {
        setPreviewFile(null);
        if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    };

    const openFilesDialog = async (sessionGuid) => {
        setFilesDialogOpen(true);
        setFilesLoading(true);
        setFilesList([]);
        setFilesSession(sessions.find(s => s.session_guid === sessionGuid) || null);
        try {
            const res = await axios.get(`${API_URL}/tendering/sessions/${sessionGuid}`, { headers });
            setFilesSession(res.data);
            setFilesList(res.data?.files || []);
        } catch (err) {
            console.error('Error fetching session files:', err);
            setFilesList([]);
        } finally {
            setFilesLoading(false);
        }
    };

    const closeFilesDialog = () => {
        setFilesDialogOpen(false);
        setFilesList([]);
        setFilesSession(null);
        closePreview();
    };

    const fetchFileBlob = async (file) => {
        const res = await axios.get(`${API_URL}/tendering/sessions/${filesSession.session_guid}/files/${file.id}/download`, {
            headers,
            responseType: 'blob'
        });
        return res.data;
    };

    const handlePreview = async (file) => {
        if (file.file_type === 'word' || file.file_type === 'excel') {
            alert('Preview is available for PDF files only. Please use Download for Word/Excel files.');
            return;
        }
        try {
            const blob = await fetchFileBlob(file);
            const url = URL.createObjectURL(blob);
            setPreviewUrl(url);
            setPreviewFile(file);
        } catch (err) {
            alert('Could not load preview: ' + (err.message || err));
        }
    };

    const handleDownload = async (file) => {
        try {
            const blob = await fetchFileBlob(file);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.file_name;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 5000);
        } catch (err) {
            alert('Download failed: ' + (err.message || err));
        }
    };

    const filteredSessions = sessions.filter(s => {
        const q = search.toLowerCase();
        const matchesSearch = (s.name || '').toLowerCase().includes(q) ||
               (s.session_guid || '').toLowerCase().includes(q) ||
               (s.created_by || '').toLowerCase().includes(q);
        const matchesCountry = filterCountry.length === 0 || filterCountry.includes(s.country_id);
        const matchesDivision = filterDivision.length === 0 || filterDivision.includes(s.division_id);
        const matchesStatus = filterStatus === 'All' || (s.work_status || 'New') === filterStatus;
        return matchesSearch && matchesCountry && matchesDivision && matchesStatus;
    });

    const handleWorkStatusChange = async (sessionGuid, newStatus) => {
        try {
            await axios.put(`${API_URL}/tendering/sessions/${sessionGuid}/work-status`,
                { work_status: newStatus },
                { headers: { ...headers, 'Content-Type': 'application/json' } }
            );
            setSessions(prev => prev.map(s =>
                s.session_guid === sessionGuid ? { ...s, work_status: newStatus } : s
            ));
        } catch (err) {
            console.error('Update status error:', err);
            alert('Could not update status: ' + (err.response?.data?.error || err.message));
        }
    };

    const clearFilters = () => {
        setSearch('');
        setFilterCountry([]);
        setFilterDivision([]);
        setFilterStatus('All');
    };

    const typeChipsFor = (ft) => TYPE_CHIPS.filter(c => ft && ft[c.key]).map(c => ({ ...c, count: ft[c.key] }));

    const summary = {
        total: sessions.length,
        files: sessions.reduce((a, s) => a + (s.file_count || 0), 0),
        inProgress: sessions.filter(s => (s.work_status || 'New') === 'In Progress').length,
        completed: sessions.filter(s => (s.work_status || 'New') === 'Completed').length,
    };

    if (loading) return <Loading />;

    return (
        <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
            <Grow in={true} timeout={500}>
                <Box sx={{ mb: 4, mt: 2, p: 4, borderRadius: 4, background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: 'white', boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)', position: 'relative', overflow: 'hidden' }}>
                    <Box sx={{ position: 'relative', zIndex: 2 }}>
                        <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
                            <UploadFileIcon sx={{ fontSize: 36, color: '#90caf9' }} />
                            Tendering
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            Upload tender documents and chat with AI to analyze bids, maps, and pricing
                        </Typography>
                    </Box>
                    <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                    <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                </Box>
            </Grow>

     

            <Card elevation={0} sx={{ mb: 4, p: 2, borderRadius: 4, border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        {['All', ...WORK_STATUSES].map(f => (
                            <Chip
                                key={f}
                                label={f}
                                onClick={() => setFilterStatus(f)}
                                sx={{
                                    fontWeight: '600',
                                    borderRadius: 2,
                                    bgcolor: filterStatus === f ? '#1e293b' : 'transparent',
                                    color: filterStatus === f ? '#ffffff' : '#64748b',
                                    border: '1px solid',
                                    borderColor: filterStatus === f ? '#1e293b' : 'transparent',
                                    '&:hover': { bgcolor: filterStatus === f ? '#0f172a' : '#f1f5f9' },
                                    transition: 'all 0.2s ease'
                                }}
                            />
                        ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Country</InputLabel>
                            <Select multiple value={filterCountry} label="Country" onChange={e => setFilterCountry(e.target.value)}
                                renderValue={(selected) => selected.length === 0 ? 'All' : selected.map(id => countries.find(c => c.id === id)?.name).join(', ')}
                                sx={{ borderRadius: 2, bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' } }}>
                                {countries.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Division</InputLabel>
                            <Select multiple value={filterDivision} label="Division" onChange={e => setFilterDivision(e.target.value)}
                                renderValue={(selected) => selected.length === 0 ? 'All' : selected.map(id => divisions.find(d => d.id === id)?.name).join(', ')}
                                sx={{ borderRadius: 2, bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' } }}>
                                {divisions.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField
                            placeholder="Search tenders..."
                            size="small"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>
                            }}
                            sx={{
                                flexGrow: 1,
                                minWidth: { sm: 220 },
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#f8fafc',
                                    borderRadius: 2,
                                    '& fieldset': { borderColor: '#e2e8f0' },
                                    '&:hover fieldset': { borderColor: '#cbd5e1' },
                                    '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '1px' },
                                }
                            }}
                        />
                        <Box sx={{ display: 'flex', gap: 2, height: '40px' }}>
                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={(e, newMode) => { if (newMode) setViewMode(newMode); }}
                                size="small"
                                sx={{
                                    '& .MuiToggleButton-root': {
                                        color: '#94a3b8',
                                        borderColor: '#e2e8f0',
                                        '&.Mui-selected': { bgcolor: '#eff6ff', color: '#3b82f6', borderColor: '#bfdbfe' },
                                        '&:hover': { bgcolor: '#f8fafc' }
                                    }
                                }}
                            >
                                <ToggleButton value="list"><ViewListIcon fontSize="small" /></ToggleButton>
                                <ToggleButton value="grid"><ViewModuleIcon fontSize="small" /></ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        <Button onClick={clearFilters} startIcon={<ClearIcon />} sx={{ fontWeight: 700, textTransform: 'none', color: '#64748b', whiteSpace: 'nowrap' }}>
                            Clear
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<UploadFileIcon />}
                            onClick={() => { resetUpload(); setOpenUpload(true); }}
                            sx={{ borderRadius: 2, fontWeight: 700, px: 3, whiteSpace: 'nowrap', background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', boxShadow: '0 8px 16px rgba(25,118,210,0.25)' }}
                        >
                            Upload Files
                        </Button>
                    </Box>
                </Box>
            </Card>

            <Grow in={true} timeout={1200}>
                <Box>
                    {viewMode === 'grid' ? (
                        <Grid container spacing={3}>
                            {filteredSessions.map((s) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={s.id}>
                                    <Card sx={{ height: '100%', borderRadius: 4, transition: 'all 0.2s', border: '1px solid rgba(0,0,0,0.04)', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 12px 28px rgba(0,0,0,0.06)', borderColor: '#cbd5e1' }, cursor: 'pointer' }} onClick={() => navigate(`/tendering/${s.session_guid}/chat`)}>
                                        <CardContent sx={{ p: 3 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                                                    <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1976d2', width: 44, height: 44, fontWeight: 'bold' }}>
                                                        {(s.name || 'T').charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    <Box sx={{ overflow: 'hidden' }}>
                                                        <Typography variant="subtitle1" fontWeight="bold" noWrap title={s.name || 'Untitled Tender'}>{s.name || 'Untitled Tender'}</Typography>
                                                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }} noWrap>{s.session_guid}</Typography>
                                                    </Box>
                                                </Box>
                                            </Box>

                                            <Box sx={{ mb: 2, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                <Chip label={s.work_status || 'New'} size="small"
                                                    sx={{ fontWeight: 'bold', bgcolor: `${WORK_STATUS_COLOR[s.work_status || 'New']}20`, color: WORK_STATUS_COLOR[s.work_status || 'New'], border: `1px solid ${WORK_STATUS_COLOR[s.work_status || 'New']}50` }} />
                                                {s.country && <Chip label={s.country} size="small" sx={{ fontWeight: 600, bgcolor: '#e8f5e9', color: '#2e7d32' }} />}
                                                {s.division && <Chip label={s.division} size="small" sx={{ fontWeight: 600, bgcolor: '#fff3e0', color: '#e65100' }} />}
                                            </Box>

                                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                {typeChipsFor(s.file_types).map((c) => (
                                                    <Chip key={c.key} size="small" icon={c.icon} label={`${c.count} ${c.label}`}
                                                        sx={{ height: 24, fontSize: '0.7rem', bgcolor: '#f1f5f9', color: c.color, fontWeight: 600 }} />
                                                ))}
                                                {s.file_count === 0 && <Typography variant="caption" color="text.secondary">No files</Typography>}
                                            </Stack>
                                        </CardContent>
                                        <Box sx={{ borderTop: '1px solid #f0f0f0', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f8fafc' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                <Typography variant="caption" color="text.secondary">{s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <Button size="small" startIcon={<FolderOpenIcon />} onClick={(e) => { e.stopPropagation(); openFilesDialog(s.session_guid); }}>Files</Button>
                                                <Button size="small" startIcon={<ChatIcon />} onClick={(e) => { e.stopPropagation(); navigate(`/tendering/${s.session_guid}/chat`); }}>Chat</Button>
                                            </Box>
                                        </Box>
                                    </Card>
                                </Grid>
                            ))}
                            {filteredSessions.length === 0 && (
                                <Grid item xs={12}>
                                    <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 4, bgcolor: 'white', border: '1px solid rgba(0,0,0,0.04)' }}>
                                        <HistoryIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1 }} />
                                        <Typography variant="h6" color="text.secondary">No tendering sessions found</Typography>
                                        <Typography variant="body2" color="text.secondary">Upload your first batch of documents to get started.</Typography>
                                    </Paper>
                                </Grid>
                            )}
                        </Grid>
                    ) : (
                        <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', bgcolor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.04)' }}>
                            <TableContainer>
                                <Table>
                                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.75rem' }}>TENDER / SESSION</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.75rem' }}>FILES</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.75rem' }}>STATUS</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.75rem' }}>COUNTRY</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.75rem' }}>DIVISION</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.75rem' }}>CREATED BY</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.75rem' }}>CREATED AT</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.75rem' }}>ACTIONS</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredSessions.map((s) => (
                                            <TableRow key={s.id} hover sx={{ '& td': { borderBottom: '1px solid #f1f5f9' } }}>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={700}>{s.name || 'Untitled Tender'}</Typography>
                                                    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#64748b' }}>{s.session_guid}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                        {typeChipsFor(s.file_types).map((c) => (
                                                            <Chip key={c.key} size="small" icon={c.icon} label={`${c.count} ${c.label}`}
                                                                sx={{ height: 24, fontSize: '0.7rem', bgcolor: '#f1f5f9', color: c.color, fontWeight: 600 }} />
                                                        ))}
                                                        {s.file_count === 0 && <Typography variant="caption" color="text.secondary">—</Typography>}
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Select
                                                        size="small"
                                                        value={s.work_status || 'New'}
                                                        onChange={(e) => handleWorkStatusChange(s.session_guid, e.target.value)}
                                                        variant="outlined"
                                                        sx={{
                                                            fontSize: '0.75rem', fontWeight: 700, minWidth: 130,
                                                            color: WORK_STATUS_COLOR[s.work_status || 'New'],
                                                            bgcolor: `${WORK_STATUS_COLOR[s.work_status || 'New']}15`,
                                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: `${WORK_STATUS_COLOR[s.work_status || 'New']}50` }
                                                        }}
                                                    >
                                                        {WORK_STATUSES.map(st => (
                                                            <MenuItem key={st} value={st} sx={{ fontSize: '0.8rem', fontWeight: 600, color: WORK_STATUS_COLOR[st] }}>{st}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </TableCell>
                                                <TableCell><Typography variant="body2">{s.country || '—'}</Typography></TableCell>
                                                <TableCell><Typography variant="body2">{s.division || '—'}</Typography></TableCell>
                                                <TableCell><Typography variant="body2">{s.created_by || '—'}</Typography></TableCell>
                                                <TableCell><Typography variant="caption" color="text.secondary">{s.created_at ? new Date(s.created_at).toLocaleString() : '—'}</Typography></TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title="View Files">
                                                        <IconButton size="small" color="info" onClick={() => openFilesDialog(s.session_guid)}>
                                                            <FolderOpenIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Open AI Chat">
                                                        <IconButton size="small" color="primary" onClick={() => navigate(`/tendering/${s.session_guid}/chat`)}>
                                                            <ChatIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {filteredSessions.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                                    <Typography variant="body2" color="text.secondary">No tendering sessions found.</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    )}
                </Box>
            </Grow>

            <Dialog open={filesDialogOpen} onClose={closeFilesDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
                <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Uploaded Files
                    <CloseIcon onClick={closeFilesDialog} sx={{ cursor: 'pointer' }} />
                </DialogTitle>
                <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f8fafc' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#64748b', mb: 1.5 }}>
                        {filesSession?.name || 'Tender'} — {filesList.length} file(s)
                    </Typography>
                    {filesLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                    ) : filesList.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No files found.</Typography>
                    ) : (
                        <Stack spacing={1}>
                            {filesList.map((f) => {
                                const meta = fileTypeMeta(f.file_type);
                                return (
                                    <Paper key={f.id} elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: 'white' }}>
                                        <Avatar sx={{ bgcolor: '#f1f5f9', color: meta.color, width: 36, height: 36 }}>{meta.icon}</Avatar>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight={600} noWrap title={f.file_name}>{f.file_name}</Typography>
                                            <Typography variant="caption" color="text.secondary">{meta.label}</Typography>
                                        </Box>
                                        <Tooltip title="Preview">
                                            <IconButton size="small" onClick={() => handlePreview(f)}><VisibilityIcon fontSize="small" /></IconButton>
                                        </Tooltip>
                                        <Tooltip title="Download">
                                            <IconButton size="small" onClick={() => handleDownload(f)}><DownloadIcon fontSize="small" /></IconButton>
                                        </Tooltip>
                                    </Paper>
                                );
                            })}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
                    <Button onClick={closeFilesDialog} sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(previewFile)} onClose={closePreview} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
                <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {previewFile?.file_name || 'Preview'}
                    <CloseIcon onClick={closePreview} sx={{ cursor: 'pointer' }} />
                </DialogTitle>
                <DialogContent sx={{ p: 0, bgcolor: '#525659', height: '78vh' }}>
                    {previewUrl ? <iframe src={previewUrl} title="preview" style={{ width: '100%', height: '100%', border: 'none' }} /> : null}
                </DialogContent>
            </Dialog>

            <Dialog open={openUpload} onClose={() => { if (!uploading) setOpenUpload(false); }} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
                <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Upload Tender Documents
                    {!uploading && <CloseIcon onClick={() => setOpenUpload(false)} sx={{ cursor: 'pointer' }} />}
                </DialogTitle>
                <DialogContent sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#f8fafc' }}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', mt: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#64748b', textTransform: 'uppercase', mb: 2 }}>Tender Details</Typography>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Tender Name (optional)"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    options={countries}
                                    getOptionLabel={(option) => option.name || ''}
                                    value={countries.find(c => c.id === countryId) || null}
                                    onChange={(e, v) => setCountryId(v ? v.id : '')}
                                    renderInput={(params) => <TextField {...params} label="Country *" required />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    options={divisions}
                                    getOptionLabel={(option) => option.name || ''}
                                    value={divisions.find(d => d.id === divisionId) || null}
                                    onChange={(e, v) => setDivisionId(v ? v.id : '')}
                                    renderInput={(params) => <TextField {...params} label="Division *" required />}
                                />
                            </Grid>
                        </Grid>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#64748b', textTransform: 'uppercase', mb: 2 }}>Documents</Typography>
                        <Grid container spacing={2}>
                            {CATEGORIES.map((cat) => (
                                <Grid item xs={12} sm={6} key={cat.key}>
                                    <Paper
                                        elevation={0}
                                        onDragOver={(e) => handleDragOver(e, cat.key)}
                                        onDragEnter={(e) => handleDragOver(e, cat.key)}
                                        onDragLeave={(e) => handleDragLeave(e, cat.key)}
                                        onDrop={(e) => handleDrop(e, cat.key)}
                                        sx={{
                                            border: dragOverCat === cat.key ? `2px dashed ${cat.color}` : '1px dashed #cbd5e1',
                                            borderRadius: 2,
                                            p: 2,
                                            height: '100%',
                                            bgcolor: dragOverCat === cat.key ? `${cat.color}12` : 'white',
                                            transition: 'all 0.2s ease',
                                            '&:hover': { borderColor: cat.color }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: cat.color, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                {cat.icon} {cat.label}
                                            </Typography>
                                            <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon />}>
                                                Select
                                                <input type="file" hidden multiple accept={cat.accept}
                                                    onChange={(e) => { handleSelectFiles(cat.key, e.target.files); e.target.value = ''; }} />
                                            </Button>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, py: 0.5 }}>
                                            <CloudUploadIcon sx={{ fontSize: 16, color: dragOverCat === cat.key ? cat.color : '#94a3b8' }} />
                                            <Typography variant="caption" sx={{ color: dragOverCat === cat.key ? cat.color : '#94a3b8', fontWeight: dragOverCat === cat.key ? 700 : 400 }}>
                                                {dragOverCat === cat.key ? 'Drop files here' : `Drag & drop ${cat.accept} here`}
                                            </Typography>
                                        </Box>
                                        <Stack spacing={0.5}>
                                            {files[cat.key].map((f) => (
                                                <Box key={f.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f8fafc', borderRadius: 1, px: 1, py: 0.5 }}>
                                                    <Typography variant="caption" noWrap sx={{ maxWidth: '85%' }}>{f.name}</Typography>
                                                    <IconButton size="small" onClick={() => removeFile(cat.key, f.id)} sx={{ p: 0.25 }}><DeleteIcon sx={{ fontSize: 16, color: '#e53935' }} /></IconButton>
                                                </Box>
                                            ))}
                                            {files[cat.key].length === 0 && <Typography variant="caption" color="text.secondary">No files selected</Typography>}
                                        </Stack>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1, bgcolor: '#f8fafc' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                        {uploading && <CircularProgress size={14} />}
                        {uploading ? uploadPhase : `${totalFileCount} file(s) selected`}
                    </Typography>
                    <Button onClick={() => setOpenUpload(false)} disabled={uploading} sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={totalFileCount === 0 || !countryId || !divisionId || uploading}
                        startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                        onClick={handleUpload}
                        sx={{ borderRadius: 2, background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', boxShadow: '0 8px 16px rgba(25, 118, 210, 0.25)', px: 3 }}
                    >
                        {uploading ? 'Please wait...' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Tendering;



